import axios from 'axios';
import { Auth } from 'aws-amplify';

const API_URL = process.env.REACT_APP_API_URL; // replace with your API URL

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const axiosInstanceLongTimeout = axios.create({
  baseURL: API_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function setupInterceptors(instance) {
  instance.interceptors.request.use(
    async config => {
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      } catch (e) {
        console.info('API request without authentication: ', e);
      }
      return config;
    },
    error => {
      Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    response => response,
    async function (error) {
      return Promise.reject(error);
    },
  );
}

// setup interceptors for both instances
setupInterceptors(axiosInstance);
setupInterceptors(axiosInstanceLongTimeout);

export const api = {
  async getUser(anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.get('/me', { params: qsParams });
  },
  async likeMessage(id, salt, uuid, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/like', { id, salt, uuid }, { params: qsParams });
  },
  async dislikeMessage(id, salt, uuid, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/dislike', { id, salt, uuid }, { params: qsParams });
  },
  async restartLike(id, salt, uuid, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/restart_like', { id, salt, uuid }, { params: qsParams });
  },
  async getAlmitraParams(anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.get('/options', { params: qsParams });
  },
  async codeExpression(anonGroup, expressionDescription, expressionType, formFields, location, cancelToken) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/code_expression',
        { expressionDescription, expressionType, formFields, location },
        { params: qsParams, cancelToken });
  },
  async likeExpression(queryId, response, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/like_expression', { query_id: queryId, response }, { params: qsParams });
  },
  async dislikeExpression(queryId, response, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/dislike_expression', { query_id: queryId, response }, { params: qsParams });
  },
  async restartLikeExpression(queryId, response, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/restart_like_expression', { query_id: queryId, response }, { params: qsParams });
  },
  async reviewQuestions(anonGroup, questions, surveyContext, surveyLocations, location, cancelToken) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }

    // call the API and return the result
    return await axiosInstanceLongTimeout.post('/review_questions',
      { questions, survey_context: surveyContext, survey_locations: surveyLocations, location },
      { params: qsParams, cancelToken });
  },
  async likeReview(reviewID, responseLength, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/like_review', { review_id: reviewID, response_len: responseLength }, { params: qsParams });
  },
  async dislikeReview(reviewID, responseLength, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/dislike_review', { review_id: reviewID, response_len: responseLength }, { params: qsParams });
  },
  async restartLikeReview(reviewID, responseLength, anonGroup) {
    let qsParams;
    if (anonGroup !== null && anonGroup.length > 0) {
      qsParams = {anon_group: anonGroup};
    }
    else {
      qsParams = {};
    }
    return await axiosInstance.post('/restart_like_review', { review_id: reviewID, response_len: responseLength }, { params: qsParams });
  },
};
